/*!
 * patternSteps.js — a line run on a signal steps its patterns, one a hit
 * © 2026 Rob Canning — GPLv3
 *
 *   screen(hue:Pseq(0, 90, 180, 270, inf), trig:every(4, beat.hit))
 *       round the colour wheel, a step a bar
 *   viz(s1, view:Pseq(polar, xy, phase, inf), trig:beat.div(2))
 *       the views in turn, a new one every 8th
 *   viz(adc, cell:Pseq(1, 2, 3, inf), stroke:Pseq(#ff2a6d, #05d9e8, inf), trig:adc.hit)
 *       two patterns, each its own step: together again every 6 hits
 *
 * A line on a signal (trig:) is run again on every hit (cueDispatcher.js),
 * its rand() and choose() rolled afresh; a pattern there gave the same
 * first value each time. Now each pattern in the line keeps its place
 * between hits and moves one step a hit -- patterns of other lengths drift
 * against each other -- and one that runs out holds its last value. A
 * stepper is made each time the line is set watching, so running the line
 * again (or editing it) starts its patterns from the top.
 *
 * A sound with a rhythm of its own (dur:) keeps its patterns: they are its
 * notes, stepped by it (synth.js), not by the hits.
 */

import { makeValueGenerator, evalMaybeRandom } from "./audio/patterns.js";

const SOUNDS = new Set(["cueSynth", "cueAudio", "cueAudioPool", "cueAudioImpulse"]);

/**
 * A stepper for one watching line: called with the line (its AST) on each
 * hit, it returns the line with every pattern at its next value.
 * @returns {(ast: object) => object}
 */
export function patternStepper() {
  const steps = new Map();          // where in the line -> { gen, last }

  const next = (where, pattern) => {
    let s = steps.get(where);
    if (!s) steps.set(where, s = { gen: makeValueGenerator(pattern), last: undefined });
    let v = s.gen?.next();
    if (v == null) return s.last;                     // run out: holds its last
    if (v && typeof v === "object" && v.type === "funcCall") v = evalMaybeRandom(v);   // Pseq(rand(1, 3), 5)
    s.last = v;
    return v;
  };

  const walk = (v, where) => {
    if (Array.isArray(v)) return v.map((x, i) => walk(x, `${where}.${i}`));
    if (!v || typeof v !== "object") return v;
    if (v.type === "pattern") return next(where, v);
    if (v.type === "signalRef" || v.type === "funcCall") return v;   // follow(), rand(): theirs
    const out = {};
    for (const k in v) out[k] = walk(v[k], `${where}.${k}`);
    return out;
  };

  return (ast) => {
    if (!Array.isArray(ast?.args)) return ast;
    if (SOUNDS.has(ast.type) && ast.args.some(a => a?.type === "dur")) return ast;
    return { ...ast, args: ast.args.map((a, i) => (a && typeof a === "object" && "value" in a ? { ...a, value: walk(a.value, `${i}:${a.type}`) } : a)) };
  };
}

/** Has the line a pattern anywhere in its values? (Only then is a stepper worth making.) */
export function hasPattern(ast) {
  const seek = v => Array.isArray(v) ? v.some(seek)
    : v && typeof v === "object" ? (v.type === "pattern" || (v.type !== "signalRef" && Object.values(v).some(seek)))
    : false;
  return (ast?.args || []).some(a => seek(a?.value));
}
